import React from 'react';
import { motion } from 'framer-motion';
import { Code, Euro, GitBranch, FileCheck, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export function ServiceInfo() {
  const { t } = useLanguage();

  const sections = [
    {
      icon: Code,
      key: 'whatWeDo',
      color: 'text-blue-400'
    },
    {
      icon: Euro,
      key: 'pricingModel',
      color: 'text-green-400'
    },
    {
      icon: GitBranch,
      key: 'workflow',
      color: 'text-purple-400'
    },
    {
      icon: FileCheck,
      key: 'terms',
      color: 'text-primary'
    },
    {
      icon: AlertTriangle,
      key: 'limitations',
      color: 'text-orange-400'
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-slate-950 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700 mb-6">
            <FileCheck size={20} className="text-slate-400" />
            <span className="text-sm font-medium text-slate-300">{t('serviceInfo.badge')}</span>
          </div>
          
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            <span className="text-white">{t('serviceInfo.title')}</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            {t('serviceInfo.subtitle')}
          </p>
        </motion.div>

        {/* Info Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {sections.map((section, index) => {
            const Icon = section.icon;
            const items = t(`serviceInfo.sections.${section.key}.items`);
            
            return (
              <motion.div
                key={section.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 ${
                  section.key === 'limitations' ? 'md:col-span-2' : ''
                }`}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className={`p-3 rounded-xl bg-slate-800/50 ${section.color}`}>
                    <Icon size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-white mb-2">
                      {t(`serviceInfo.sections.${section.key}.title`)}
                    </h3>
                    {t(`serviceInfo.sections.${section.key}.description`) && (
                      <p className="text-slate-400 text-sm">
                        {t(`serviceInfo.sections.${section.key}.description`)}
                      </p>
                    )}
                  </div>
                </div>

                <ul className={`space-y-3 ${section.key === 'limitations' ? 'grid md:grid-cols-2 gap-4' : ''}`}>
                  {items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-300">
                      <span className={`text-xs mt-1 ${section.color}`}>▪</span>
                      <span className="text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Footer note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-slate-500 max-w-3xl mx-auto">
            {t('serviceInfo.note')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
